import { LinkIcon, UnlinkIcon } from "@xola/icons";
import { Button, Modal, SubmitButton, Tag } from "@xola/ui-kit";
import { DateFormat, formatDate, Loader } from "common";
import dayjs from "dayjs";
import throttle from "p-throttle";
import React, { useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { Except } from "type-fest";
import { Purchase, PurchaseItem, Waiver } from "types";
import { useDebounce } from "use-debounce";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInfiniteCollection } from "../../../hooks/useInfiniteCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useResource } from "../../../hooks/useResource";
import { NoTranslate } from "../../Common/NoTranslate";
import { PromptModal } from "../../Common/PromptModal";
import { SearchInput } from "../../Common/SearchInput";
import { WaiverParticipantDetails } from "../../Dashboard/Roster/WaiverParticipantDetails";

interface WaiverModalInterface {
    purchaseItem: PurchaseItem;
    isOpen: boolean;
    onClose: () => void;
}

export const LinkWaiverModal = (props: WaiverModalInterface) => {
    const handleClose = () => {
        props.onClose();
    };

    return (
        <Modal isOpen={props.isOpen} className="max-w-[1000px]" onClose={handleClose}>
            <ModalContent {...props} />
        </Modal>
    );
};

interface ModalContentProps {
    purchaseItem: PurchaseItem;
    isOpen: boolean;
    onClose: () => void;
}

export const ModalContent = ({ onClose, purchaseItem: activePurchaseItem }: Except<ModalContentProps, "isOpen">) => {
    const seller = useSeller();
    const reusePastWaiver = seller.preferences?.waiver?.multiLinkDayInterval !== undefined;
    // pass unlinked as true if reusePastWaiver is false (not enabled) or if multiLinkDayInterval is set
    const unlinked = !!seller.preferences?.waiver?.multiLinkDayInterval || !reusePastWaiver;

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch] = useDebounce(searchQuery, 1000);
    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
    const [selectedWaiver, setSelectedWaiver] = useState<Waiver | null>(null);

    const [newLinkWaiver, setNewLinkedWaiver] = useState<Waiver[]>([]);
    const [unLinkedIds, setUnlinkIds] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const invalidate = useInvalidate();

    const { data: purchase, isLoading: isLoadingPurchase } = useResource<Purchase>(
        ["purchases", activePurchaseItem?.purchase.id],
        { include: "form", expand: "waiver" },
    );

    const fetchParams = searchQuery.length > 0 ? { search: debouncedSearch } : null;

    const {
        data: waivers,
        hasNextPage,
        fetchNextPage,
        isLoading: isLoadingWaivers,
    } = useInfiniteCollection<Waiver>("waivers", { unlinked, ...fetchParams });

    if (isLoadingPurchase) {
        return <Loader />;
    }

    const purchaseItem = purchase?.items.find((item) => item.id === activePurchaseItem.id) ?? purchase?.items[0];

    const linkedWaivers = purchaseItem?.waivers ?? [];

    const handleLinkWaiver = (waiver: Waiver) => {
        setNewLinkedWaiver([...newLinkWaiver, waiver]);
        setUnlinkIds(unLinkedIds.filter((x) => x !== waiver.id));
    };

    const handleUnLinkWaiver = () => {
        if (selectedWaiver) {
            setIsOpenConfirmDialog(false);
            setUnlinkIds([...unLinkedIds, selectedWaiver.id]);
            setNewLinkedWaiver(newLinkWaiver.filter((x) => x.id !== selectedWaiver.id));
        }
    };

    const handleSubmit = async () => {
        if (purchase) {
            setIsLoading(true);
            if (newLinkWaiver.length > 0) {
                for (const waiver of newLinkWaiver) {
                    await linkWaiverToPurchase(waiver.id, purchase.id, purchaseItem?.id);
                }
            }

            if (unLinkedIds.length > 0) {
                for (const id of unLinkedIds) {
                    await unLinkThrottle(id, purchase.id);
                }
            }

            invalidate("purchases", purchase.id);
            if (purchaseItem?.id) invalidate("purchaseItems", purchaseItem.id);
            invalidate("timeslots");
            if (purchaseItem?.event?.id) invalidate("events", purchaseItem.event.id);
            setIsLoading(false);
            onClose();
        }
    };

    const searchText = searchQuery.trim().toLowerCase();
    const searchNumber = searchText.replace(/\D/g, "");
    const filteredLinkWaivers = [...linkedWaivers, ...waivers]
        .filter((waiver, index, self) => {
            const isLinked = purchaseItem?.waivers?.some((itemWaiver) => itemWaiver.id === waiver.id);
            const isStagedToLink = newLinkWaiver.some((newWaiver) => newWaiver.id === waiver.id);

            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            const isLinkedOrStaged = isLinked || isStagedToLink;

            const isStagedToUnlink = unLinkedIds.includes(waiver.id);
            const alreadyExist = self.findIndex((item) => item.id === waiver.id) !== index;

            return (isStagedToUnlink || !isLinkedOrStaged) && !alreadyExist;
        })
        .filter((waiver) =>
            waiver.participants.some(
                (participant) =>
                    participant.customerName.toLowerCase().includes(searchText) ||
                    (participant.customerEmail ?? "").toLowerCase().includes(searchText) ||
                    (participant.phone ?? "").replace(/\D/g, "").includes(searchNumber),
            ),
        );

    const filteredLinkedWaivers = [...newLinkWaiver, ...linkedWaivers].filter((waiver) => {
        const isStagedToUnlink = unLinkedIds.includes(waiver.id);

        return !isStagedToUnlink;
    });

    return (
        <>
            <Modal.Header>Link a Waiver</Modal.Header>
            <Modal.Body>
                <div className="flex">
                    <div className="flex-1">
                        <div className="mr-8">
                            <div className="text-lg font-bold leading-5">All Waivers</div>
                            <div className="mt-4">
                                <SearchInput
                                    size="medium"
                                    placeholder="Search by name, email, or phone"
                                    onChange={(searchResult: string) => setSearchQuery(searchResult)}
                                />
                            </div>
                            <div className="mt-6">
                                {filteredLinkWaivers.length > 0 ? (
                                    <Virtuoso
                                        style={{ height: "350px" }}
                                        data={filteredLinkWaivers}
                                        endReached={async () => {
                                            if (hasNextPage) {
                                                fetchNextPage();
                                            }
                                        }}
                                        itemContent={(_, waiver) => (
                                            <LinkWaiverItem
                                                waiver={waiver}
                                                unLinkedIds={unLinkedIds}
                                                onItemClick={() => handleLinkWaiver(waiver)}
                                            />
                                        )}
                                    />
                                ) : isLoadingWaivers ? (
                                    <Loader />
                                ) : (
                                    <div className="flex justify-center">
                                        <span className="pb-4 pt-4 text-lg font-bold text-black">No results found</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 border-l-2 border-gray-light">
                        <div className="ml-8">
                            <div className="text-lg font-bold leading-5">Linked Waivers</div>
                            <div className="mt-8">
                                <Virtuoso
                                    style={{ height: "400px" }}
                                    data={filteredLinkedWaivers}
                                    itemContent={(_, waiver) => (
                                        <UnLinkWaiverItem
                                            waiver={waiver}
                                            onItemClick={() => {
                                                setSelectedWaiver(waiver);
                                                setIsOpenConfirmDialog(true);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="space-x-4">
                <Button color="secondary" variant="outline" onClick={onClose}>
                    Cancel
                </Button>
                <SubmitButton
                    color="primary"
                    disabled={newLinkWaiver.length === 0 && unLinkedIds.length === 0}
                    isLoading={isLoading}
                    onClick={handleSubmit}
                >
                    Save
                </SubmitButton>
            </Modal.Footer>

            <ConfirmationModal
                isOpen={isOpenConfirmDialog}
                onClose={() => {
                    setIsOpenConfirmDialog(false);
                    setSelectedWaiver(null);
                }}
                onConfirm={handleUnLinkWaiver}
            />
        </>
    );
};

const LinkWaiverItem = ({
    waiver,
    onItemClick,
    unLinkedIds,
}: {
    waiver: Waiver;
    unLinkedIds: ID[];
    onItemClick: () => void;
}) => {
    const userTimeZone = dayjs.tz.guess();
    const isStagedToUnlink = unLinkedIds.includes(waiver.id);
    const linkedWaiversCount = waiver.purchases?.length;
    const completeLinkedCount =
        linkedWaiversCount && isStagedToUnlink
            ? linkedWaiversCount - 1
            : !linkedWaiversCount && isStagedToUnlink
            ? 0
            : linkedWaiversCount ?? 0;

    return (
        <div
            className="group flex w-full cursor-pointer items-center justify-between whitespace-nowrap rounded border-b border-gray-lighter bg-transparent py-3 px-2 hover:bg-gray-lighter"
            onClick={onItemClick}
        >
            <div className="mr-auto flex items-center">
                <WaiverParticipantDetails waiver={waiver} size="tiny" nameMaxLength={20} />

                {completeLinkedCount > 0 && (
                    <div className="ml-2 mr-4 flex items-center justify-center rounded-full bg-gray-lighter px-2 py-1 text-gray-dark ">
                        <LinkIcon size="sm" />
                        <NoTranslate as="div" className="ml-1 text-sm">
                            {completeLinkedCount}
                        </NoTranslate>
                    </div>
                )}
            </div>

            <div className="text-base tracking-tighter text-gray-dark">
                {formatDate(dayjs(waiver?.createdAt).tz(userTimeZone), DateFormat.DATE_TIME_SHORT)}
            </div>
        </div>
    );
};

const UnLinkWaiverItem = ({ waiver, onItemClick }: { waiver: Waiver; onItemClick: () => void }) => {
    const userTimeZone = dayjs.tz.guess();

    return (
        <Tag
            className="group flex w-full cursor-pointer items-center justify-between whitespace-nowrap border-white border-b-gray-lighter bg-transparent px-5 pt-3 pb-3 text-sm leading-4 hover:border-primary hover:bg-primary-lighter"
            color="primary"
            size="medium"
        >
            <WaiverParticipantDetails waiver={waiver} size="tiny" nameMaxLength={20} />
            <div className="flex items-center justify-between">
                <div className="mx-4 tracking-tighter">
                    {formatDate(dayjs(waiver?.createdAt).tz(userTimeZone), DateFormat.DATE_TIME_SHORT)}
                </div>
                <div className="w-4">
                    <UnlinkIcon className="hidden group-hover:block" size="tiny" onClick={onItemClick} />
                </div>
            </div>
        </Tag>
    );
};

const ConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
}: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
}) => {
    return (
        <div>
            <PromptModal
                title="Unlink Waiver"
                // isLoading={isLoading}
                isOpen={isOpen}
                color="primary"
                onConfirm={onConfirm}
                onClose={onClose}
            >
                <div className="text-center text-gray-darker">
                    Are you sure you want to unlink this from this booking?
                </div>
                <p>Once you unlink this waiver you will be able to link it to another booking.</p>
            </PromptModal>
        </div>
    );
};

const unLinkThrottle = throttle({ limit: 1, interval: 200 })(async (id: ID, purchaseId: ID) => {
    await api.put(`/waivers/${id}`, {
        purchase: purchaseId,
        remove: true,
    });
});

export const linkWaiverToPurchase = throttle({ limit: 1, interval: 200 })(
    async (id: ID, purchaseId: ID, purchaseItem?: ID, guestId?: ID, participantId?: ID) => {
        await api.put(`/waivers/${id}`, {
            purchase: purchaseId,
            item: purchaseItem,
            ...(guestId ? { guest: { id: guestId } } : null),
            ...(participantId ? { participant: { id: participantId } } : null),
        });
    },
);
